<template>
  <!-- BLOCK layout: under a remarks editor. Rendered only while a cue is set. -->
  <div v-if="layout === 'block'">
    <div v-if="theCue" class="field-cue" role="status" aria-live="polite">

      <p class="field-cue-line">
        <i class="fa fa-user-pen text-warning" aria-hidden="true"></i>
        {{changedLine}}
      </p>

      <p class="field-cue-hint"><small class="text-muted">{{dirty || isDirty ? dirtyText : cleanText}}</small></p>

      <el-button type="text" size="mini" @click="showTheirs = !showTheirs">{{showTheirs ? hideLabel : showLabel}}</el-button>
      <el-button type="text" size="mini" @click="take">{{takeLabel}}</el-button>

      <div v-if="showTheirs" class="value-box" role="region" tabindex="0" :aria-label="'Their ' + label">
        {{theirText}}
        <el-button type="text" size="mini" :aria-label="copyAria" @click="copyTheirs">
          <i class="fa fa-copy" aria-hidden="true"></i>&nbsp;{{copied ? copyDone : copyLabel}}
        </el-button>
      </div>

    </div>
  </div>

  <!-- INLINE layout: a fixed-width slot right after the control, inside the same
       el-form-item, so an inline form never reflows (XOL-416). -->
  <span v-else class="field-cue-slot">
    <el-popover
      v-if="theCue"
      v-model="open"
      trigger="manual"
      placement="top-start"
      width="280"
      popper-class="xol-field-cue-pop">
      <div class="field-cue" @keydown.esc.stop="onEscInside">
        <p class="field-cue-line">
          <i class="fa fa-user-pen text-warning" aria-hidden="true"></i>
          {{changedLine}}
        </p>
        <p class="field-cue-hint"><small class="text-muted">{{dirty || isDirty ? dirtyText : cleanText}}</small></p>
        <p v-if="theirText !== ''">{{theirsLabel}} <b>{{theirText}}</b></p>
        <el-button type="text" size="mini" @click="take">{{takeLabel}}</el-button>
        <el-button type="text" size="mini" @click="open = false">{{closeLabel}}</el-button>
      </div>
      <el-button
        slot="reference"
        ref="trigger"
        type="text"
        size="mini"
        :aria-label="iconAria"
        @click="open = !open">
        <i class="fa fa-user-pen text-warning" aria-hidden="true"></i>
      </el-button>
    </el-popover>
    <span v-if="theCue" class="field-cue-sr" role="status" aria-live="polite">{{changedLine}}</span>
  </span>
</template>

<script>
/*
 * C-11 of the XOL-596 UX spec: the "changed underneath you" cue (plan R2.19).
 *
 * Shown while a field the user is editing was changed by someone else: the
 * adopt never replaces the field, it raises this cue instead. Never takes focus
 * or moves the caret. Two layouts:
 *
 *   block   under every remarks (HtmlInput) editor: who and when, Show / Hide
 *           their version (with Copy), and Use their version
 *   inline  TextInput, raw el-input, selects, checkboxes, switches, dates: an
 *           icon in a fixed 1.5em slot, with a popover that opens by itself while
 *           the field is focused and otherwise on a click
 *
 * Give it `row` + `path` (it reads the cue from lib/requestRowSync and does
 * "Use their version" itself), or an explicit `cue` and listen to `take`.
 */
import DateMixins from '@/mixins/DateMixins'
import * as sync from '@/lib/requestRowSync'
import { copyText } from '@/lib/requestSyncCopy'
import { plainText, describeEntry, describeScalar, valueKind } from '@/lib/syncText'

export default {
  name: 'field-cue',
  mixins: [DateMixins],

  // The LayerHost's registry, when this renders inside a drawer. Only used to claim Esc
  // (registerEscClaim); null outside a drawer.
  inject: {
    drawerDirtyRegistry: {
      default: null
    }
  },

  props: {
    row: {
      type: Object,
      default: null
    },
    path: {
      type: String,
      default: null
    },
    cue: {
      type: Object,
      default: null
    },
    label: {
      type: String,
      default: 'this field'
    },
    kind: {
      type: String,
      default: null
    },
    layout: {
      type: String,
      default: 'inline'
    },
    dirty: {
      type: Boolean,
      default: false
    }
  },

  data () {
    return {
      open: false,
      showTheirs: false,
      copied: false,
      showLabel: copyText('requestSync.cue.show'),
      hideLabel: copyText('requestSync.cue.hide'),
      takeLabel: copyText('requestSync.cue.take'),
      closeLabel: copyText('requestSync.cue.close'),
      theirsLabel: copyText('requestSync.cue.theirs'),
      dirtyText: copyText('requestSync.cue.dirty'),
      cleanText: copyText('requestSync.cue.clean'),
      copyLabel: copyText('requestSync.copy.button'),
      copyDone: copyText('requestSync.copy.done')
    }
  },

  computed: {

    theCue () {
      if (this.cue) {
        return this.cue
      }
      return (this.row && this.path) ? sync.fieldCue(this.row, this.path) : null
    },

    isDirty () {
      return (this.row && this.path) ? sync.fieldDirty(this.row, this.path) : false
    },

    changedLine () {
      var c = this.theCue
      if (!c || !c.by) {
        return copyText('requestSync.cue.changedUnknown')
      }
      return copyText('requestSync.cue.changed', {
        by: c.by,
        org: c.org,
        datetime: c.at ? this.formatDateTime(c.at) : null
      })
    },

    theirKind () {
      return this.kind || (this.theCue ? valueKind(this.path || '', this.theCue.serverValue) : 'scalar')
    },

    // Their value as plain text. Never user HTML.
    theirText () {
      var c = this.theCue
      if (!c) {
        return ''
      }
      if (this.theirKind === 'text') {
        return plainText(c.serverValue)
      }
      if (this.theirKind === 'list') {
        return (c.serverValue || []).map(describeEntry).join(', ')
      }
      return describeScalar(this.path || '', c.serverValue, { date: this.formatDate })
    },

    iconAria () {
      return copyText('requestSync.cue.iconLabel', { label: this.label, by: this.theCue ? this.theCue.by : null })
    },

    copyAria () {
      return copyText('requestSync.copy.ariaTheirs', { label: this.label })
    }

  },

  created () {
    // Bound once: the same function is registered and unregistered.
    var self = this
    this._escClaim = function () {
      if (!self.open) {
        return false
      }
      self.open = false
      return true
    }
  },

  beforeDestroy () {
    this.setEscClaim(false)
  },

  watch: {
    // While the popover is open it owns Esc inside the drawer: the first Esc closes only the
    // cue (focus stays where it is), the next one reaches the drawer. See FieldCue.md.
    open (now) {
      this.setEscClaim(now && this.layout !== 'block')
    },

    // The popover opens by itself when the cue first appears on a focused field;
    // otherwise only on a click. It never takes focus.
    theCue (now, was) {
      if (now && !was && this.layout !== 'block' && this.row && this.path && sync.fieldFocused(this.row, this.path)) {
        this.open = true
      }
      if (!now) {
        this.open = false
        this.showTheirs = false
      }
    }
  },

  methods: {

    setEscClaim (on) {
      var reg = this.drawerDirtyRegistry
      if (!reg || typeof reg.registerEscClaim !== 'function') {
        return
      }
      if (on) {
        reg.registerEscClaim(this._escClaim)
      } else {
        reg.unregisterEscClaim(this._escClaim)
      }
    },

    // Esc with focus inside the popover (Use their version / Close): close the cue, put focus
    // on its icon button, and stop the key there so it does not also close the drawer.
    onEscInside () {
      this.open = false
      var trigger = this.$refs.trigger
      if (trigger && trigger.$el && typeof trigger.$el.focus === 'function') {
        trigger.$el.focus()
      }
    },

    // "Use their version" (UF11): the field takes the parked server value, its
    // base moves to that version, the cue clears. The caret stays where it is.
    take () {
      if (this.row && this.path) {
        sync.takeServerValue(this.row, this.path)
      }
      this.open = false
      this.showTheirs = false
      this.$emit('take')
    },

    copyTheirs () {
      var self = this
      var text = this.theirText

      var done = function () {
        self.copied = true
        setTimeout(function () { self.copied = false }, 2000)
      }

      if (typeof navigator !== 'undefined' && navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(text).then(done, function () {})
        return
      }

      try {
        var ta = document.createElement('textarea')
        ta.value = text
        document.body.appendChild(ta)
        ta.select()
        document.execCommand('copy')
        document.body.removeChild(ta)
        done()
      } catch (e) {
        // select-and-copy by hand
      }
    }

  }
}
</script>

<style scoped>
/* Fixed width: the cue icon appears without moving the inline form (XOL-416). */
.field-cue-slot { display: inline-block; width: 1.5em; vertical-align: middle; }
.field-cue-sr { position: absolute; width: 1px; height: 1px; overflow: hidden; clip: rect(0, 0, 0, 0); white-space: nowrap; }
.field-cue { font-size: 13px; line-height: 1.4; }
.field-cue-line, .field-cue-hint { margin: 0 0 4px; }
.value-box {
  white-space: pre-wrap;
  max-height: 8em;
  overflow-y: auto;
  border: 1px solid #e4e7ed;
  border-radius: 3px;
  padding: 4px 8px;
  background: #fafafa;
}
</style>
